import { Link, useNavigate, useParams } from 'react-router-dom';
import PageList from '../../templates/PageList';
import BadgeStatus from '../../atoms/BadgeStatus';
import { useMemo, useState } from 'react';
import { IoTrash } from 'react-icons/io5';
import { toast } from 'react-toastify';
import Dialog from '../../organisms/Dialog';
import {
    STRUCTURING_ROUTES,
    STRUCTURING_BREADCRUMBS,
    formatCurrency,
    formatDate,
    getStatusColor,
    getStatusLabel,
    OperationStatus,
} from './structuringUtils';
import {
    Operation,
    buildFetchUrl,
    buildFetchHeaders,
    buildTableHeaders,
    deleteOperation,
} from './OperationPageListBackend';

const OperationPageList = () => {
    const navigate = useNavigate();
    const { page = '0', pageSize = '20' } = useParams();
    const [showDeleteDialog, setShowDeleteDialog] = useState(false);
    const [selectedOperation, setSelectedOperation] = useState<Operation | null>(null);
    const [isDeleting, setIsDeleting] = useState(false);
    const [refreshKey, setRefreshKey] = useState(0);

    const url = buildFetchUrl(String(parseInt(page, 10) || 0), String(parseInt(pageSize, 10) || 20));
    const fetchHeaders = useMemo(() => buildFetchHeaders(), []);
    const tableHeaders = useMemo(() => buildTableHeaders(), []);

    const handleDeleteClick = (operation: Operation) => {
        setSelectedOperation(operation);
        setShowDeleteDialog(true);
    };

    const confirmDelete = async () => {
        if (!selectedOperation) return;

        setIsDeleting(true);
        try {
            const result = await deleteOperation(selectedOperation.id);
            
            if (result.success) {
                toast.success(result.message);
                setShowDeleteDialog(false);
                setSelectedOperation(null);
                setRefreshKey(prev => prev + 1);
            } else {
                toast.error(result.message);
            }
        } finally {
            setIsDeleting(false);
        }
    };

    const canDelete = (operation: Operation): boolean => {
        return operation.status !== OperationStatus.CLOSED;
    };

    const renderRows = (operations: any) => {
        if (!operations || !Array.isArray(operations)) {
            return [];
        }

        return operations.map((operation: Operation, index: number) => (
            <tr key={operation.id} className={`${index % 2 === 0 ? 'bg-slate-100' : ''} hover:bg-slate-300 hover:shadow-md border-b border border-gray-300`}>
                <td className="py-2 px-2 whitespace-nowrap">{operation.operationCode || 'N/A'}</td>
                <td className="py-2 px-2 whitespace-nowrap">{operation.assignorName || 'N/A'}</td>
                <td className="py-2 px-2 whitespace-nowrap">{operation.totalDocuments || 0}</td>
                <td className="py-2 px-2 whitespace-nowrap">{operation.totalReceivables || 0}</td>
                <td className="py-2 px-2 whitespace-nowrap">{formatCurrency(operation.totalAmount || 0)}</td>
                <td className="py-2 px-2 whitespace-nowrap">{formatDate(operation.processingDate)}</td>
                <td className="py-2 px-2 whitespace-nowrap">
                    <BadgeStatus
                        color={getStatusColor(operation.status)}
                        text={getStatusLabel(operation.status)}
                    />
                </td>
                <td className="py-2 px-2 text-center whitespace-nowrap">
                    <div className="flex justify-center space-x-2">
                        <button
                            onClick={() => navigate(STRUCTURING_ROUTES.view(operation.id))}
                            className="bg-gray-600 hover:bg-gray-700 dark:bg-gray-500 dark:hover:bg-gray-600 text-white font-bold py-1 px-3 rounded text-sm whitespace-nowrap"
                        >
                            Gerenciar
                        </button>
                        <button
                            onClick={() => handleDeleteClick(operation)}
                            disabled={!canDelete(operation)}
                            className="bg-red-600 hover:bg-red-700 dark:bg-red-500 dark:hover:bg-red-600 text-white font-bold py-1 px-3 rounded text-sm whitespace-nowrap disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-1"
                            title={!canDelete(operation) ? 'Operação fechada não pode ser excluída' : 'Excluir operação'}
                        >
                            <IoTrash />
                        </button>
                    </div>
                </td>
            </tr>
        ));
    };

    const pageChanged = (newPage: number) => {
        navigate(`/structuring/operations/${newPage}/${pageSize}`, { replace: true });
    };

    const pageSizeChanged = (newPageSize: number) => {
        navigate(`/structuring/operations/0/${newPageSize}`, { replace: true });
    };

    return (
        <>
            <PageList
                pageTitle="Lista de Operações"
                url={url}
                fetchHeaders={fetchHeaders}
                breadcrumb={[STRUCTURING_BREADCRUMBS.operations]}
                tableHeaders={tableHeaders}
                defaultBackendHeader={false}
                tableFieldSort=""
                tableOrderSort="ASC"
                onTableSort={() => {}}
                renderRows={renderRows}
                page={parseInt(page) || 0}
                pageSize={parseInt(pageSize) || 20}
                onPageChange={pageChanged}
                onItemsPerPageChange={pageSizeChanged}
                itemsLabel="operações"
                key={refreshKey}
            >
                {{
                    filterSlot: null,
                    controlSlot: (
                        <div className='pt-4'>
                            <Link to={STRUCTURING_ROUTES.create}>
                                <button
                                    type='button'
                                    className='text-white bg-gradient-to-r from-blue-500 via-blue-600 to-blue-700 hover:bg-gradient-to-br focus:ring-4 focus:outline-none focus:ring-blue-300 dark:focus:ring-blue-800 font-medium rounded text-sm px-5 py-2.5 text-center mr-2 mb-2'
                                >
                                    Nova Operação
                                </button>
                            </Link>
                        </div>
                    )
                }}
            </PageList>
            <Dialog
                id="delete-confirmation"
                show={showDeleteDialog}
                onClose={() => setShowDeleteDialog(false)}
                header="Confirmar Exclusão"
                body={
                    <div className="p-6">
                        <p className="text-gray-700 dark:text-gray-300">
                            Tem certeza que deseja excluir a operação <strong>{selectedOperation?.operationCode}</strong>?
                        </p>
                        <p className="text-gray-600 dark:text-gray-400 text-sm mt-2">
                            Esta ação não pode ser desfeita e todos os documentos e recebíveis serão removidos.
                        </p>
                    </div>
                }
                footer={
                    <div className="flex space-x-2">
                        <button
                            type="button"
                            onClick={() => setShowDeleteDialog(false)}
                            className="px-4 py-2 text-gray-700 bg-gray-200 hover:bg-gray-300 rounded dark:bg-gray-600 dark:text-gray-300 dark:hover:bg-gray-500"
                            disabled={isDeleting}
                        >
                            Cancelar
                        </button>
                        <button
                            type="button"
                            onClick={confirmDelete}
                            className="px-4 py-2 text-white bg-red-600 hover:bg-red-700 rounded disabled:opacity-50"
                            disabled={isDeleting}
                        >
                            {isDeleting ? 'Excluindo...' : 'Excluir'}
                        </button>
                    </div>
                }
            />
        </>
    );
};

export default OperationPageList;
